/**
 * Sysadmin Studio Catalog → Categories (scaffolding).
 *
 * Lists sysadmin-suggested category defaults (organization_id IS NULL
 * rows in studio_categories). Tenants see these in their Studio
 * filter dropdowns + can create their own tenant-scoped categories
 * on top.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Plus, Trash2, Tags, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useSysAdminCategories,
  useCreateSysAdminCategory,
  useDeleteSysAdminCategory,
  type SysCategory,
} from '@/lib/use-sysadmin-system-catalog';

export const Route = createFileRoute('/sysadmin/studio/categories')({
  component: SysadminCategoriesPage,
});

const KINDS = ['tool', 'skill', 'prompt', 'agent'] as const;
type Kind = (typeof KINDS)[number];

function SysadminCategoriesPage() {
  const { data, isLoading, error } = useSysAdminCategories();
  const create = useCreateSysAdminCategory();
  const del = useDeleteSysAdminCategory();
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<SysCategory | null>(null);
  const [form, setForm] = useState<{ kind: Kind; name: string; description: string; sortOrder: number }>(
    { kind: 'tool', name: '', description: '', sortOrder: 100 },
  );

  const items = useMemo(() => data?.items ?? [], [data]);
  const grouped = useMemo(() => {
    const g: Record<Kind, SysCategory[]> = { tool: [], skill: [], prompt: [], agent: [] };
    for (const c of items) {
      const k = c.kind as Kind;
      if (g[k]) g[k].push(c);
    }
    return g;
  }, [items]);

  const handleCreate = () => {
    if (!form.name.trim()) return toast.error('Name is required.');
    create.mutate({
      kind: form.kind,
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      sortOrder: form.sortOrder,
    }, {
      onSuccess: () => {
        toast.success(`Category "${form.name.trim()}" added`);
        setCreating(false);
        setForm({ kind: 'tool', name: '', description: '', sortOrder: 100 });
      },
      onError: (err) => toast.error('Create failed', { description: err.message }),
    });
  };

  const handleDelete = () => {
    if (!confirmDelete) return;
    del.mutate({ id: confirmDelete.id }, {
      onSuccess: () => { toast.success(`Category "${confirmDelete.name}" removed`); setConfirmDelete(null); },
      onError: (err) => toast.error('Delete failed', { description: err.message }),
    });
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Tags className="h-4 w-4 text-muted-foreground" />
            Category Suggestions
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Sysadmin-suggested category defaults (Q10). Tenants see these in their Studio filter dropdowns and can create their own tenant-scoped categories on top.
          </p>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setCreating(true)}>
          <Plus className="h-3.5 w-3.5" /> New category
        </Button>
      </div>

      {isLoading ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground animate-pulse">Loading…</CardContent></Card>
      ) : error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>Failed to load: {error.message}</div>
          </CardContent>
        </Card>
      ) : (
        KINDS.map((k) => (
          <Card key={k}>
            <CardContent className="px-0">
              <div className="px-4 pt-3 pb-1 flex items-center gap-2">
                <Badge variant="outline" className="text-[10px] capitalize">{k}</Badge>
                <span className="text-[11px] text-muted-foreground">{grouped[k].length} suggestion{grouped[k].length === 1 ? '' : 's'}</span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-20">Order</TableHead>
                    <TableHead className="w-20 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {grouped[k].length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-xs text-muted-foreground py-4 italic">No {k} suggestions yet.</TableCell></TableRow>
                  ) : grouped[k].map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="text-sm font-medium">{c.name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{c.description ?? '—'}</TableCell>
                      <TableCell className="text-xs tabular-nums">{c.sortOrder}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10" onClick={() => setConfirmDelete(c)} title="Remove">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))
      )}

      <Sheet open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>New category suggestion</SheetTitle>
            <SheetDescription>Adds a sysadmin-suggested default. Tenants always see these in their filter dropdown alongside their own categories.</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 py-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Kind</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v as Kind })}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => <SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="text-sm h-8" placeholder="reliability" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="text-sm h-8" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Sort order</Label>
              <Input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} className="text-sm h-8" />
            </div>
            <div className="flex justify-end gap-2 pt-3">
              <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={create.isPending}>{create.isPending ? 'Saving…' : 'Add'}</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmDelete !== null} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove category suggestion</AlertDialogTitle>
            <AlertDialogDescription>
              Removes the suggestion for <code className="bg-muted px-1 rounded">{confirmDelete?.kind}.{confirmDelete?.name}</code>. Tenants who already used this category keep using it (rows reference the string, not this row's id).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={handleDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
